import { useLogto } from '@logto/react';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { queryClient } from '@/app/providers/query-provider';
import { savafAppUrl } from '@/configs/app-url';
import { setSupabaseAccessToken, setSupabaseDbToken } from '@/configs/supabase';
import { clearRumUser, setRumUser } from '@/lib/openobserve-rum';
import { Sentry } from '@/lib/sentry';
import { traceSpan } from '@/lib/tracing';
import {
  beginInitialResolve,
  GRANT_FAILED_MESSAGE,
  hasAccessToken,
  releaseInitialResolveGate,
  shouldResolveSessionAfterTokenSettle,
  shouldStartInitialResolve,
  tokenLoadingAfterInitialResolveSettle,
} from './initial-resolve';
import { getPermissionsForRole, hasPermission, type Permission, resolveRole, type SavaFRole } from './roles';

const SESSION_REFRESH_MS = Number(import.meta.env.VITE_SESSION_REFRESH_MS) || 45 * 60 * 1000;
const FOCUS_REFRESH_MIN_MS = Number(import.meta.env.VITE_FOCUS_REFRESH_MIN_MS) || 5 * 60 * 1000;

export type RefreshResult = { logtoToken: string; supabaseToken: string } | null;

interface IdTokenClaims {
  sub: string;
  email?: string;
  systemRole?: string;
  system_role?: string;
  workspaceRole?: string;
  workspace_role?: string;
  workspaceId?: string;
  workspace_id?: string;
}

export interface TenancyMembership {
  curatorOrgId: string;
  savapWorkspaceId: string;
  displayName: string;
  role: SavaFRole;
  isActive: boolean;
  logoUrl: string | null;
}

export type SessionErrorCode = 'NO_ENTITLEMENTS' | 'ORG_NOT_AUTHORISED' | 'GRANT_FAILED' | undefined;

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  userId: string | undefined;
  email: string | undefined;
  displayName: string | undefined;
  role: SavaFRole | undefined;
  curatorOrgId: string | undefined;
  permissions: ReadonlySet<Permission>;
  accessToken: string | undefined;
  supabaseToken: string | undefined;
  /** Token for Edge Function API calls — Supabase HS256 token (with app_role + org_id) after session resolution, falls back to Logto token */
  apiToken: string | undefined;
  memberships: TenancyMembership[];
  activeTenancy: TenancyMembership | undefined;
  switchTenancy: (orgId: string) => Promise<boolean>;
  sessionError: string | undefined;
  sessionErrorCode: SessionErrorCode;
  hasPermission: (permission: Permission) => boolean;
  refreshSession: () => Promise<RefreshResult>;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export const useAuth = (): AuthState => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};

const EMPTY_PERMISSIONS: ReadonlySet<Permission> = new Set();

interface TestAuthGlobal {
  isAuthenticated: boolean;
  isLoading: boolean;
  claims?: IdTokenClaims;
  accessToken?: string;
  sessionError?: string;
  sessionErrorCode?: SessionErrorCode;
}

function getTestAuth(): TestAuthGlobal | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as Record<string, unknown>).__TEST_AUTH__ as TestAuthGlobal | undefined;
}

const noop = async () => {};
const failedSwitch = async () => false;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const testAuth = getTestAuth();
  if (testAuth) {
    return <TestAuthProvider testAuth={testAuth}>{children}</TestAuthProvider>;
  }
  return <LogtoAuthProvider>{children}</LogtoAuthProvider>;
};

const TestAuthProvider = ({ testAuth, children }: { testAuth: TestAuthGlobal; children: ReactNode }) => {
  const role = useMemo(() => (testAuth.claims ? resolveRole(testAuth.claims) : undefined), [testAuth.claims]);

  const curatorOrgId = useMemo(() => {
    if (!testAuth.claims) return undefined;
    if (role === 'platform_admin') return undefined;
    return testAuth.claims.workspaceId ?? testAuth.claims.workspace_id;
  }, [testAuth.claims, role]);

  const permissions = useMemo(() => (role ? getPermissionsForRole(role) : EMPTY_PERMISSIONS), [role]);

  const checkPermission = useCallback((permission: Permission) => (role ? hasPermission(role, permission) : false), [role]);

  const state = useMemo<AuthState>(
    () => ({
      isAuthenticated: testAuth.isAuthenticated,
      isLoading: testAuth.isLoading,
      userId: testAuth.claims?.sub,
      email: testAuth.claims?.email,
      displayName: undefined,
      role,
      curatorOrgId,
      permissions,
      accessToken: testAuth.accessToken,
      supabaseToken: undefined,
      apiToken: testAuth.accessToken,
      memberships: [],
      activeTenancy: undefined,
      switchTenancy: failedSwitch,
      sessionError: testAuth.sessionError,
      sessionErrorCode: testAuth.sessionErrorCode,
      hasPermission: checkPermission,
      refreshSession: async () => null,
      signIn: noop,
      signOut: noop,
    }),
    [testAuth, role, curatorOrgId, permissions, checkPermission],
  );

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
};

interface ResolvedSession {
  role: SavaFRole;
  curatorOrgId: string | null;
  userId: string;
  email: string;
  displayName: string;
  supabaseToken: string | null;
  memberships: TenancyMembership[];
  activeMembership: { curatorOrgId: string; savapWorkspaceId: string; role: string } | null;
}

interface SessionError {
  error: string | null;
  code: SessionErrorCode;
}

interface SupabaseTokenClaims {
  app_role?: unknown;
  org_id?: unknown;
}

function decodeSupabaseTokenClaims(token: string): SupabaseTokenClaims | null {
  try {
    const encodedPayload = token.split('.')[1];
    if (!encodedPayload) return null;
    const base64 = encodedPayload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(encodedPayload.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(base64), character => character.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes));
    return typeof claims === 'object' && claims !== null ? (claims as SupabaseTokenClaims) : null;
  } catch {
    return null;
  }
}

function hasConsistentTenancy(session: ResolvedSession, requestedOrgId?: string): boolean {
  if (!session.supabaseToken) return false;
  const claims = decodeSupabaseTokenClaims(session.supabaseToken);
  if (!claims || claims.app_role !== session.role) return false;

  if (session.role === 'platform_admin') {
    return session.curatorOrgId === null && session.activeMembership === null && claims.org_id == null;
  }

  const activeMembership = session.activeMembership;
  const activeMemberships = session.memberships.filter(membership => membership.isActive);
  return (
    !!session.curatorOrgId &&
    !!activeMembership &&
    activeMemberships.length === 1 &&
    activeMemberships[0].curatorOrgId === activeMembership.curatorOrgId &&
    session.curatorOrgId === activeMembership.curatorOrgId &&
    claims.org_id === activeMembership.curatorOrgId &&
    activeMembership.role === session.role &&
    (!requestedOrgId || activeMembership.curatorOrgId === requestedOrgId)
  );
}

function getRouteOrgId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const orgId = window.location.pathname.match(/^\/admin\/org\/([^/]+)/)?.[1];
  return orgId && /^[0-9a-f-]{36}$/i.test(orgId) ? orgId : undefined;
}

function fetchSession(token: string, requestedOrgId?: string): Promise<{ session: ResolvedSession | null } & SessionError> {
  return traceSpan(
    'auth.resolve-session',
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: session resolution validates HTTP, identity, tenancy, and token consistency in one security boundary
    async () => {
      const supabaseUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:54321';
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      let url = `${supabaseUrl}/functions/v1/resolve-session`;
      if (requestedOrgId) url += `?orgId=${encodeURIComponent(requestedOrgId)}`;
      try {
        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: anonKey,
          },
        });
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          let code: SessionErrorCode;
          try {
            const parsed = JSON.parse(body);
            if (parsed.code === 'NO_ENTITLEMENTS' || parsed.code === 'ORG_NOT_AUTHORISED') {
              code = parsed.code;
            }
          } catch {
            /* body is not JSON */
          }
          const msg = `resolve-session ${res.status}: ${body}`;
          if (code !== 'NO_ENTITLEMENTS') {
            Sentry.captureMessage(msg, { level: 'warning', tags: { action: 'resolve_session' } });
          }
          return { session: null, error: msg, code };
        }
        const data = await res.json();
        const resolvedSession: ResolvedSession = {
          role: data.user.role as SavaFRole,
          curatorOrgId: data.user.curatorOrgId ?? null,
          userId: data.user.id,
          email: data.user.email,
          displayName: data.user.displayName,
          supabaseToken: data.supabaseToken ?? null,
          memberships: (data.memberships ?? []).map(
            (m: {
              curatorOrgId: string;
              savapWorkspaceId: string;
              displayName: string;
              role: string;
              isActive: boolean;
              logoUrl?: string | null;
            }) => ({
              ...m,
              role: m.role as SavaFRole,
              logoUrl: m.logoUrl ?? null,
            }),
          ),
          activeMembership: data.activeMembership ?? null,
        };
        if (!hasConsistentTenancy(resolvedSession, requestedOrgId)) {
          const msg = 'resolve-session returned inconsistent tenancy claims';
          Sentry.captureMessage(msg, {
            level: 'error',
            tags: { action: 'resolve_session', reason: 'tenancy_claim_mismatch' },
            extra: {
              requestedOrgId: requestedOrgId ?? null,
              resolvedOrgId: resolvedSession.curatorOrgId,
              activeOrgId: resolvedSession.activeMembership?.curatorOrgId ?? null,
              resolvedRole: resolvedSession.role,
            },
          });
          return { session: null, error: msg, code: undefined };
        }
        return {
          session: resolvedSession,
          error: null,
          code: undefined,
        };
      } catch (e) {
        const msg = `resolve-session fetch failed: ${e instanceof Error ? e.message : String(e)}`;
        Sentry.captureException(e, { tags: { action: 'resolve_session' } });
        return { session: null, error: msg, code: undefined };
      }
    },
  );
}

function toRefreshResult(logtoToken: string, session: ResolvedSession | null): RefreshResult {
  if (!session?.supabaseToken) return null;
  return { logtoToken, supabaseToken: session.supabaseToken };
}

const LogtoAuthProvider = ({ children }: { children: ReactNode }) => {
  const {
    isAuthenticated,
    isLoading,
    getAccessToken,
    getIdTokenClaims,
    signIn,
    signOut: logtoSignOut,
    clearAllTokens,
  } = useLogto();
  const [accessToken, setAccessToken] = useState<string | undefined>();
  const [claims, setClaims] = useState<IdTokenClaims | undefined>();
  const [session, setSession] = useState<ResolvedSession | null>(null);
  const [memberships, setMemberships] = useState<TenancyMembership[]>([]);
  const [sessionError, setSessionError] = useState<string | undefined>();
  const [sessionErrorCode, setSessionErrorCode] = useState<SessionErrorCode>();
  const [sessionResolved, setSessionResolved] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const fetchingRef = useRef(false);
  const tokenFailureRecoveredRef = useRef(false);
  const lastResolvedRef = useRef(0);
  const activeOrgIdRef = useRef<string | undefined>(undefined);

  const recoverFromTokenFailure = useCallback(() => {
    if (tokenFailureRecoveredRef.current) return;
    tokenFailureRecoveredRef.current = true;

    setAccessToken(undefined);
    setSupabaseAccessToken(undefined);
    setSupabaseDbToken(undefined);
    setClaims(undefined);
    setSession(null);
    setMemberships([]);
    setSessionError(GRANT_FAILED_MESSAGE);
    setSessionErrorCode('GRANT_FAILED');
    setSessionResolved(true);
    activeOrgIdRef.current = undefined;
    void clearAllTokens();
  }, [clearAllTokens]);

  const silentRefresh = useCallback(
    (): Promise<RefreshResult> =>
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: refresh must atomically coordinate token, session, tenancy, and error state
      traceSpan('auth.silent-refresh', async () => {
        if (fetchingRef.current || !isAuthenticated) return null;
        fetchingRef.current = true;
        try {
          const resource = import.meta.env.VITE_LOGTO_API_RESOURCE || undefined;
          const token = await getAccessToken(resource);
          if (!token) {
            recoverFromTokenFailure();
            return null;
          }
          setAccessToken(token);
          setSupabaseAccessToken(token);
          const { session: resolved, error, code } = await fetchSession(token, getRouteOrgId() ?? activeOrgIdRef.current);
          if (resolved) {
            tokenFailureRecoveredRef.current = false;
            activeOrgIdRef.current = resolved.curatorOrgId ?? undefined;
            setSupabaseDbToken(resolved.supabaseToken ?? undefined);
            setSession(resolved);
            setMemberships(resolved.memberships);
            setSessionError(undefined);
            setSessionErrorCode(undefined);
          } else if (error) {
            setSupabaseDbToken(undefined);
            setSessionError(error);
            setSessionErrorCode(code);
          }
          lastResolvedRef.current = Date.now();
          return toRefreshResult(token, resolved);
        } catch (err) {
          Sentry.captureException(err, { tags: { action: 'silent_refresh' } });
          recoverFromTokenFailure();
          return null;
        } finally {
          fetchingRef.current = false;
        }
      }),
    [isAuthenticated, getAccessToken, recoverFromTokenFailure],
  );

  useEffect(() => {
    if (!shouldStartInitialResolve(isAuthenticated, fetchingRef.current)) return;

    let cancelled = false;
    beginInitialResolve(fetchingRef);
    setTokenLoading(true);

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: initial resolution coordinates token, session, tenancy, and recovery state
    const resolve = async () => {
      const resource = import.meta.env.VITE_LOGTO_API_RESOURCE || undefined;
      const [token, idClaims] = await Promise.all([getAccessToken(resource), getIdTokenClaims()]);
      if (!shouldResolveSessionAfterTokenSettle(cancelled)) return;
      if (!hasAccessToken(token)) {
        recoverFromTokenFailure();
        return;
      }
      setAccessToken(token);
      setSupabaseAccessToken(token);
      setClaims(idClaims as IdTokenClaims | undefined);

      const { session: resolved, error, code } = await fetchSession(token, getRouteOrgId());
      if (!shouldResolveSessionAfterTokenSettle(cancelled)) return;
      activeOrgIdRef.current = resolved?.curatorOrgId ?? undefined;
      if (resolved) tokenFailureRecoveredRef.current = false;
      setSupabaseDbToken(resolved?.supabaseToken ?? undefined);
      setSession(resolved);
      setMemberships(resolved?.memberships ?? []);
      setSessionError(error ?? undefined);
      setSessionErrorCode(code);
      setSessionResolved(true);
      lastResolvedRef.current = Date.now();
    };

    resolve()
      .catch(err => {
        Sentry.captureException(err, { tags: { action: 'initial_auth_resolve' } });
        if (!shouldResolveSessionAfterTokenSettle(cancelled)) return;
        recoverFromTokenFailure();
      })
      .finally(() => {
        if (cancelled) return;
        releaseInitialResolveGate(fetchingRef);
        setTokenLoading(tokenLoadingAfterInitialResolveSettle());
      });

    return () => {
      cancelled = true;
      releaseInitialResolveGate(fetchingRef);
      setTokenLoading(tokenLoadingAfterInitialResolveSettle());
    };
  }, [isAuthenticated, getAccessToken, getIdTokenClaims, recoverFromTokenFailure]);

  // Sync Sentry + OpenObserve RUM user context with resolved session
  useEffect(() => {
    if (session) {
      Sentry.setUser({ id: session.userId, email: session.email });
      setRumUser({ id: session.userId, role: session.role, curatorOrgId: session.curatorOrgId ?? undefined });
    } else if (sessionResolved) {
      Sentry.setUser(null);
      clearRumUser();
    }
  }, [session, sessionResolved]);

  useEffect(() => {
    if (!sessionResolved || !isAuthenticated) return;
    const id = setInterval(silentRefresh, SESSION_REFRESH_MS);
    return () => clearInterval(id);
  }, [sessionResolved, isAuthenticated, silentRefresh]);

  useEffect(() => {
    if (!sessionResolved || !isAuthenticated) return;
    const onFocus = () => {
      if (Date.now() - lastResolvedRef.current >= FOCUS_REFRESH_MIN_MS) {
        silentRefresh();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [sessionResolved, isAuthenticated, silentRefresh]);

  useEffect(() => {
    if (!import.meta.env.DEV && !import.meta.env.VITE_SESSION_REFRESH_MS) return;
    (window as unknown as Record<string, unknown>).__SAVAF_SESSION_DEBUG__ = {
      forceLastResolved: (ts: number) => {
        lastResolvedRef.current = ts;
      },
    };
    return () => {
      delete (window as unknown as Record<string, unknown>).__SAVAF_SESSION_DEBUG__;
    };
  }, []);

  const role = useMemo(() => {
    if (session) return session.role;
    if (sessionResolved) return undefined;
    return claims ? resolveRole(claims) : undefined;
  }, [session, sessionResolved, claims]);

  const curatorOrgId = useMemo(() => {
    if (session) return session.curatorOrgId ?? undefined;
    if (!claims) return undefined;
    if (role === 'platform_admin') return undefined;
    return claims.workspaceId ?? claims.workspace_id;
  }, [session, claims, role]);

  const permissions = useMemo(() => (role ? getPermissionsForRole(role) : EMPTY_PERMISSIONS), [role]);

  const checkPermission = useCallback((permission: Permission) => (role ? hasPermission(role, permission) : false), [role]);

  const activeTenancy = useMemo(
    () => memberships.find(m => m.isActive) ?? memberships.find(m => m.curatorOrgId === session?.curatorOrgId),
    [memberships, session?.curatorOrgId],
  );

  const switchTenancy = useCallback(
    async (orgId: string) => {
      if (!accessToken || fetchingRef.current) return false;
      fetchingRef.current = true;
      try {
        const { session: resolved, error, code } = await fetchSession(accessToken, orgId);
        if (resolved) {
          // Stop old-org requests before installing a token scoped to the new org.
          await queryClient.cancelQueries();
          activeOrgIdRef.current = resolved.curatorOrgId ?? undefined;
          setSupabaseDbToken(resolved.supabaseToken ?? undefined);
          setSession(resolved);
          setMemberships(resolved.memberships);
          setSessionError(undefined);
          setSessionErrorCode(undefined);
          // Clear all TanStack Query caches to prevent cross-org data leakage
          queryClient.clear();
          // Update Sentry context
          Sentry.setUser({ id: resolved.userId, email: resolved.email });
          // Update OpenObserve RUM context
          setRumUser({ id: resolved.userId, role: resolved.role, curatorOrgId: resolved.curatorOrgId ?? undefined });
          return true;
        } else if (error) {
          setSessionError(error);
          setSessionErrorCode(code);
        }
        return false;
      } finally {
        fetchingRef.current = false;
      }
    },
    [accessToken],
  );

  const handleSignIn = useCallback(() => signIn(savafAppUrl('/callback')), [signIn]);

  const handleSignOut = useCallback(async () => {
    Sentry.setUser(null);
    clearRumUser();
    setAccessToken(undefined);
    setSupabaseAccessToken(undefined);
    setSupabaseDbToken(undefined);
    setClaims(undefined);
    setSession(null);
    setMemberships([]);
    setSessionError(undefined);
    setSessionErrorCode(undefined);
    setSessionResolved(false);
    activeOrgIdRef.current = undefined;
    tokenFailureRecoveredRef.current = false;
    await logtoSignOut(savafAppUrl('/'));
  }, [logtoSignOut]);

  const state = useMemo<AuthState>(
    () => ({
      isAuthenticated,
      isLoading: isLoading || tokenLoading || (isAuthenticated && !sessionResolved),
      userId: session?.userId ?? claims?.sub,
      email: session?.email ?? claims?.email,
      displayName: session?.displayName,
      role,
      curatorOrgId,
      permissions,
      accessToken,
      supabaseToken: session?.supabaseToken ?? undefined,
      apiToken: session?.supabaseToken ?? accessToken,
      memberships,
      activeTenancy,
      switchTenancy,
      sessionError,
      sessionErrorCode,
      hasPermission: checkPermission,
      refreshSession: silentRefresh,
      signIn: handleSignIn,
      signOut: handleSignOut,
    }),
    [
      isAuthenticated,
      isLoading,
      tokenLoading,
      sessionResolved,
      session,
      sessionError,
      sessionErrorCode,
      claims,
      role,
      curatorOrgId,
      permissions,
      accessToken,
      memberships,
      activeTenancy,
      switchTenancy,
      checkPermission,
      silentRefresh,
      handleSignIn,
      handleSignOut,
    ],
  );

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
};
